// ============================================================================
// NuLinkDevice — Nu-Link HID Flash Programmer (stub)
// ============================================================================
//
// All flash operations are stubbed with descriptive errors.
// Real implementation requires Wireshark capture verification of the
// protocol against the official Nuvoton NuMicro ICP Programming Tool.

import type {
  HIDFlashProgrammer,
  ProgrammerInfo,
  ChipDefinition,
  DeviceIdResult,
  ConfigBytes,
  FlashProgress8051,
  ProgrammerEvent,
} from '@/types/hid8051';
import { HidTransport } from '../HidTransport';
import {
  NULINK_HID_FILTERS,
  NULINK_REPORT_SIZE,
  NuLinkCmd,
} from './constants';
import { buildPacket, parseResponse } from './protocol';

const STUB_ERROR = 'Nu-Link protocol stub: requires Wireshark capture verification before real implementation';

type EventHandler = (...args: unknown[]) => void;

export class NuLinkDevice implements HIDFlashProgrammer {
  readonly programmerFamily = 'nulink' as const;
  private transport: HidTransport;
  private protocolVersion: 1 | 2;
  private _connected = false;
  private eventHandlers: Map<ProgrammerEvent, EventHandler[]> = new Map();

  private constructor(transport: HidTransport, protocolVersion: 1 | 2 = 1) {
    this.transport = transport;
    this.protocolVersion = protocolVersion;
  }

  get connected(): boolean {
    return this._connected;
  }

  /**
   * Request a Nu-Link device via WebHID and create an instance.
   */
  static async requestDevice(): Promise<NuLinkDevice> {
    const devices = await navigator.hid.requestDevice({
      filters: NULINK_HID_FILTERS,
    });

    if (devices.length === 0) {
      throw new Error('No Nu-Link programmer selected');
    }

    const device = devices[0];
    const transport = new HidTransport(device, NULINK_REPORT_SIZE);
    await transport.open();

    // Detect protocol version from PID
    const isV2 = device.productId >= 0x5200;
    return new NuLinkDevice(transport, isV2 ? 2 : 1);
  }

  async connect(): Promise<ProgrammerInfo> {
    // GET_VERSION to verify communication
    const packet = buildPacket(NuLinkCmd.GET_VERSION, {}, this.protocolVersion);
    const response = await this.transport.sendAndReceive(packet);
    const parsed = parseResponse(response, NuLinkCmd.GET_VERSION, this.protocolVersion);

    this._connected = true;

    const info: ProgrammerInfo = {
      name: this.transport.productName || 'Nu-Link',
      firmwareVersion: parsed.param1,
      vid: this.transport.vendorId,
      pid: this.transport.productId,
      protocolVersion: this.protocolVersion,
    };

    this.emit('connected');
    return info;
  }

  async disconnect(): Promise<void> {
    if (this._connected) {
      try {
        const packet = buildPacket(NuLinkCmd.DISCONNECT, {}, this.protocolVersion);
        await this.transport.sendReport(packet);
      } catch {
        // Best-effort disconnect
      }
    }
    await this.transport.close();
    this._connected = false;
    this.emit('disconnected');
  }

  async setConfig(
    _chip: ChipDefinition,
    _clockKHz?: number,
    _vccMv?: number
  ): Promise<void> {
    throw new Error(STUB_ERROR);
  }

  async readDeviceId(): Promise<DeviceIdResult> {
    throw new Error(STUB_ERROR);
  }

  async readConfig(): Promise<ConfigBytes> {
    throw new Error(STUB_ERROR);
  }

  async massErase(): Promise<void> {
    throw new Error(STUB_ERROR);
  }

  async eraseRange(_address: number, _size: number): Promise<void> {
    throw new Error(STUB_ERROR);
  }

  async writeFlash(
    _address: number,
    _data: Uint8Array,
    _onProgress?: (p: FlashProgress8051) => void
  ): Promise<void> {
    throw new Error(STUB_ERROR);
  }

  async readFlash(
    _address: number,
    _length: number,
    _onProgress?: (p: FlashProgress8051) => void
  ): Promise<Uint8Array> {
    throw new Error(STUB_ERROR);
  }

  async verifyFlash(
    _address: number,
    _data: Uint8Array,
    _onProgress?: (p: FlashProgress8051) => void
  ): Promise<boolean> {
    throw new Error(STUB_ERROR);
  }

  async resetToAprom(): Promise<void> {
    throw new Error(STUB_ERROR);
  }

  async resetToLdrom(): Promise<void> {
    throw new Error(STUB_ERROR);
  }

  on(event: ProgrammerEvent, handler: EventHandler): void {
    const handlers = this.eventHandlers.get(event) ?? [];
    handlers.push(handler);
    this.eventHandlers.set(event, handlers);
  }

  private emit(event: ProgrammerEvent, ...args: unknown[]): void {
    const handlers = this.eventHandlers.get(event) ?? [];
    for (const handler of handlers) {
      handler(...args);
    }
  }
}
